import { Location } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Inject, Injectable, inject } from '@angular/core';
import { App } from '@df/core/app';
import { MultisiteService } from '@df/core/service/multisite';
import { WINDOW } from '@df/dom/globals';
import { NCore } from '../df-core.interfaces';

export interface IPathRule {
    from: RegExp;
    to: string;
    lowerCaseExt?: boolean;
}

/**
 * Register service in app
 */
declare module '@df/core/app' {
    interface App {
        url: Url;
    }
}

@Injectable({
    providedIn: 'root'
})
export class Url {
    private window = inject(WINDOW);

    private multisiteService = inject(MultisiteService);
    private app = inject(App);
    private location = inject(Location);
    private moduleConfig = inject(NCore.OPTIONS);

    readonly storeUrl = this.window.location.protocol + '//' + this.window.location.host + this.multisiteService.base;

    constructor() {
        this.app.url = this;
    }

    /**
     * Converts path to full media url
     */
    toMediaUrl(input: string, host: string = this.app.dfConfig.endpoint.media): string {
        if (!input || typeof input !== 'string') {
            return input;
        }

        if (input.match(/^http/i)) {
            return input;
        }

        const pathRules: IPathRule[] = [
            ...this.moduleConfig.url.pathRules,
            {
                from: /^\/cms\//,
                to: '/static/cms/',
                lowerCaseExt: true
            },
            {
                from: /^\/catalog\//,
                to: '/static/media/catalog/'
            },
            {
                from: /^\/product\//,
                to: '/static/media/catalog/product/'
            },
            {
                from: /^\/buffer\//,
                to: '/static/media/buffer/'
            },
            {
                from: /^\/swatch\//,
                to: '/static/media/attribute/swatch/'
            },
            {
                from: /^\/category\//,
                to: '/static/media/catalog/category/'
            },
            {
                from: /^\/storepickup\//,
                to: '/static/media/magestore/storepickup/images/store/gallery/'
            },
            {
                from: /^(\/*)magestore\/storepickup\/images\/tag\//,
                to: '/static/media/magestore/storepickup/images/tag/'
            },
            {
                from: /^(\/*)magenest\/giftregistry\//,
                to: '/static/media/magenest/giftregistry/'
            },
            {
                from: /^\/media\//,
                to: '/static/'
            },
            {
                from: /^\/front\//,
                to: '/images/'
            },
            {
                from: /^\/quote\//,
                to: '/static/media/quote/'
            },
            {
                from: /^\/common\//,
                to: '/assets/images-common/'
            },
            {
                from: /^\/hub\//,
                to: '/static/media/hub/'
            }
        ];

        const rule = pathRules.find(i => i.from.test(input));

        if (rule) {
            input = input.replace(rule.from, rule.to);
            if (rule.lowerCaseExt) {
                input = input.replace(/\.([a-z0-9]+)$/i, ext => ext.toLowerCase());
            }
        }

        return host + '/' + input.replace(/^\//, '');
    }

    private _baseSegments?: string[];

    /**
     * Every store's base as a bare segment.
     *
     * Read from config rather than `MultisiteService.all`, which filters by ACL - a
     * store the visitor cannot browse is still one whose base must not survive in a path.
     */
    private get baseSegments(): string[] {
        if (!this._baseSegments) {
            this._baseSegments = Object.values(this.app.dfConfig?.ms?.sites ?? {})
                .map(site => (site.base ?? '').replace(/^\/+|\/+$/g, '').toLowerCase())
                .filter(Boolean);
        }
        return this._baseSegments;
    }

    private leadingBase(path: string): string | undefined {
        const [segment] = path.split(/[/?#]/);
        return segment && this.baseSegments.includes(segment.toLowerCase()) ? segment : undefined;
    }

    /**
     * Strips leading store bases from a relative path.
     *
     * Anything that prepends a base - `toUrl()`, `<href>` - doubles it when the input
     * already carries one, giving `/uk/uk/...`. Both forms genuinely reach that code:
     * the router reports base-stripped paths, while content and API payloads are
     * authored with the base included. Stripping repeats because paths carrying more
     * than one base have been observed in live content.
     */
    sanitize(input: string): string {
        if (!input || input.includes(':')) {
            return input;
        }

        const isRooted = input.startsWith('/');
        let path = input.replace(/^\/+/, '');

        for (let base = this.leadingBase(path); base; base = this.leadingBase(path)) {
            path = path.slice(base.length).replace(/^\/+/, '');
        }

        return isRooted ? '/' + path : path;
    }

    /**
     * Provieds absolute url
     * if host is not set then current store is used
     * if host is string then its considered as host part
     * if host is number then its considered as any store id
     *
     */
    toUrl(input: string, host: string | number = this.storeUrl): string {
        if (input === undefined) {
            return input;
        }

        if (input.includes('://')) {
            return input;
        }

        input = this.sanitize(input);

        if (typeof host === 'number') {
            const site = this.multisiteService.toMultisiteItem(host);

            if (site) {
                host = this.window.location.protocol + '//' + (site.domain || this.window.location.host) + (site.base || '/');
            } else {
                return '';
            }
        }
        host = host.replace(/\/$/, '') + '/';
        return host + input.replace(/^\/+/, '');
    }

    /**
     * Gets current path (including search params)
     */
    getPath(): string {
        return this.location.path().replace(new RegExp('^' + this.multisiteService.base), '/');
    }

    getSearch(prefix = true): string | undefined {
        const search = this.getPath().split('?')[1];
        return prefix && search ? '?' + search : search;
    }
    /**
     * Retrives search http params
     */
    getSearchParams() {
        return new HttpParams({ fromString: this.getSearch(false) });
    }
}
